/**
 * The customer's brand kit on a customer-facing screen (suite rule: the customer's own logo, colours and font,
 * never a kit SkyDrive designs). Shared by the hangar screen and the owner page's live preview.
 *
 * What a kit changes on the hangar screen: the logo beside the site name, the font, a rule under the header
 * (primary colour) and a marker on the observations header (highlight colour). It never touches the limit
 * colours: red, amber and green always mean the state of the owner's limits (DEC-164).
 */
import { useEffect, type CSSProperties } from 'react'
import type { Brand } from './types.js'

/** Web fonts a kit may name: the same list the API accepts. */
export const BRAND_FONTS: readonly string[] = [
  'Barlow Semi Condensed',
  'IBM Plex Sans',
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Oswald',
  'Poppins',
  'Source Sans 3',
]

/** The logo for a dark screen: the dark-background logo, else the light one. */
export function brandLogo(brand: Brand | null | undefined): string | null {
  return brand?.logoDark ?? brand?.logoLight ?? null
}

/** True when the logo drawn on a dark screen is the light-background one, which needs a light plate behind it. */
export function logoNeedsPlate(brand: Brand | null | undefined): boolean {
  return brand?.logoDark == null && brand?.logoLight != null
}

/** CSS variables and font for the screen's root. Empty for no kit, so the SkyWeather look stands. */
export function brandStyle(brand: Brand | null | undefined): CSSProperties {
  if (!brand) return {}
  const style: Record<string, string> = {}
  if (brand.primary) style['--brand-primary'] = brand.primary
  if (brand.accent) style['--brand-accent'] = brand.accent
  if (brand.font && BRAND_FONTS.includes(brand.font)) style['fontFamily'] = `'${brand.font}', 'IBM Plex Sans', system-ui, sans-serif`
  return style as CSSProperties
}

/** Load the kit's font from Google Fonts once; a missing network just leaves the fallback font. */
export function useBrandFont(font: string | null | undefined): void {
  useEffect(() => {
    if (!font || !BRAND_FONTS.includes(font)) return
    const id = `brand-font-${font.replace(/\W+/g, '-').toLowerCase()}`
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font).replace(/%20/g, '+')}:wght@500;600;700&display=swap`
    document.head.appendChild(link)
  }, [font])
}
